// TanStack Table v9 feature registration. Only the features declared here are
// included in the bundle, so every data table in the app shares this one set.
import {
  columnFilteringFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString,
  metaHelper,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from "@tanstack/react-table";

// Every column def across the app sets `meta: { label }` (the column-
// visibility dropdown's display name - see DataTable.tsx). V9 types column
// meta as a type-only feature slot (metaHelper), not via the v8-style
// `declare module { interface ColumnMeta {...} }` augmentation - that older
// pattern doesn't compile against v9's generic ColumnMeta<TFeatures, TData,
// TValue> (see @tanstack/table-core's TypeScript skill doc).
//
// `mobile` controls where a column lands in DataTable's phone card layout
// (below md, rows render as cards instead of a sideways-scrolling table).
// Usually left unset - the defaults cover most columns:
//   - a column with id "status"        → "badge"   (top-right of the card)
//   - a column with no `label`         → "actions" (card footer)
//   - the first other labelled column  → "title"   (card heading)
//   - everything else                  → "field"   (label/value grid)
// Set it explicitly to override, or "hidden" to leave a column off cards.
export type MobileSlot = "title" | "badge" | "field" | "actions" | "hidden";
// `fullWidth` (only meaningful on a "field" column) spans both columns of
// the phone card's label/value grid instead of sharing a row with the next
// field - e.g. UserManagement's Designation column, whose value needs the
// full card width to fit its Admin/Finance Approver badges on the same
// line as the designation text. Defaults to false (half-width, the
// existing behaviour) for every column that doesn't set it.
export type AppColumnMeta = {
  label?: string;
  mobile?: MobileSlot;
  fullWidth?: boolean;
};

export const features = tableFeatures({
  columnFilteringFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns: { includesString: filterFn_includesString },
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    text: sortFn_text,
    datetime: sortFn_datetime,
  },
  columnMeta: metaHelper<AppColumnMeta>(),
});

export type AppTableFeatures = typeof features;
